import type { Json } from "../primitives";

/**
 * What kind of thing a field holds.
 *
 * The operator registry is keyed off this, so adding a field to a module is a
 * declaration — name, label, type — and the filter builder knows what to
 * offer without being told twice.
 */
export type FieldType =
  | "text"
  | "number"
  | "money"
  | "date"
  | "datetime"
  | "boolean"
  | "enum"
  | "tag"
  | "reference";

/**
 * Every operator the builder can offer.
 *
 * One flat union rather than one per field type: a condition is stored,
 * shared in a saved view and encoded into a URL, and a single vocabulary is
 * what lets all three round-trip without a per-type decoder.
 */
export type FilterOperator =
  // Any type
  | "is_empty"
  | "is_not_empty"
  // Text
  | "is"
  | "is_not"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  // Ordered — number, money, date, datetime
  | "eq"
  | "neq"
  | "lt"
  | "lte"
  | "gt"
  | "gte"
  | "between"
  // Date only
  | "in_last_days"
  | "in_next_days"
  | "is_today"
  | "in_last_calendar_days"
  // Enum and reference
  | "is_any_of"
  | "is_none_of"
  // Tag
  | "has_tag"
  | "not_has_tag"
  | "has_any_tag"
  // Boolean
  | "is_true"
  | "is_false";

/**
 * How many values an operator takes.
 *
 * The builder renders the value input from this, which is why it lives beside
 * the operator rather than in the UI: `between` needing two inputs is a fact
 * about the operator, not about the screen drawing it.
 */
export type OperatorArity = "none" | "one" | "two" | "many";

export interface OperatorDefinition {
  operator: FilterOperator;
  /** What the builder shows. Reads as a sentence with the field name. */
  label: string;
  arity: OperatorArity;
}

const EMPTINESS: OperatorDefinition[] = [
  { operator: "is_empty", label: "is empty", arity: "none" },
  { operator: "is_not_empty", label: "is not empty", arity: "none" },
];

const ORDERED: OperatorDefinition[] = [
  { operator: "eq", label: "=", arity: "one" },
  { operator: "neq", label: "≠", arity: "one" },
  { operator: "lt", label: "<", arity: "one" },
  { operator: "lte", label: "≤", arity: "one" },
  { operator: "gt", label: ">", arity: "one" },
  { operator: "gte", label: "≥", arity: "one" },
  { operator: "between", label: "is between", arity: "two" },
];

const DATE_RELATIVE: OperatorDefinition[] = [
  { operator: "in_last_days", label: "is in the last (days)", arity: "one" },
  { operator: "in_next_days", label: "is in the next (days)", arity: "one" },
];

/**
 * Calendar-day operators, evaluated against the store's timezone
 * (`STORE_TIMEZONE`) rather than a rolling window from the instant the query
 * runs — "today" and "in the last 7 days" mean the calendar day and the
 * calendar week a person in the store's timezone is standing in, not the
 * last 24 or 168 hours (#406). `in_last_days` above stays a rolling window on
 * purpose: elsewhere in the app ("shipments in the last 3 days") a rolling
 * window is what people mean, and only the Dashboard's "today"/"this week"
 * tiles need a calendar one.
 */
const DATE_CALENDAR: OperatorDefinition[] = [
  { operator: "is_today", label: "is today", arity: "none" },
  {
    operator: "in_last_calendar_days",
    label: "is in the last (calendar days)",
    arity: "one",
  },
];

/**
 * Which operators each field type offers (FR-VUE-3).
 *
 * The builder offers only valid operators for the chosen field, which is what
 * stops "shipping state contains 3" from being expressible at all rather than
 * merely returning nothing.
 */
export const OPERATORS_BY_FIELD_TYPE: Record<FieldType, OperatorDefinition[]> =
  {
    text: [
      { operator: "is", label: "is", arity: "one" },
      { operator: "is_not", label: "is not", arity: "one" },
      { operator: "contains", label: "contains", arity: "one" },
      { operator: "not_contains", label: "does not contain", arity: "one" },
      { operator: "starts_with", label: "starts with", arity: "one" },
      { operator: "ends_with", label: "ends with", arity: "one" },
      ...EMPTINESS,
    ],
    number: [...ORDERED, ...EMPTINESS],
    money: [...ORDERED, ...EMPTINESS],
    date: [...ORDERED, ...DATE_RELATIVE, ...DATE_CALENDAR, ...EMPTINESS],
    datetime: [...ORDERED, ...DATE_RELATIVE, ...DATE_CALENDAR, ...EMPTINESS],
    boolean: [
      { operator: "is_true", label: "is yes", arity: "none" },
      { operator: "is_false", label: "is no", arity: "none" },
    ],
    enum: [
      { operator: "is", label: "is", arity: "one" },
      { operator: "is_not", label: "is not", arity: "one" },
      { operator: "is_any_of", label: "is any of", arity: "many" },
      { operator: "is_none_of", label: "is none of", arity: "many" },
      ...EMPTINESS,
    ],
    tag: [
      { operator: "has_tag", label: "has", arity: "one" },
      { operator: "not_has_tag", label: "does not have", arity: "one" },
      { operator: "has_any_tag", label: "has any of", arity: "many" },
      ...EMPTINESS,
    ],
    reference: [
      { operator: "is", label: "is", arity: "one" },
      { operator: "is_not", label: "is not", arity: "one" },
      { operator: "is_any_of", label: "is any of", arity: "many" },
      { operator: "is_none_of", label: "is none of", arity: "many" },
      ...EMPTINESS,
    ],
  };

/** One option of an `enum` field, or of a `tag` field with a known set. */
export interface FieldOption {
  value: string;
  label: string;
}

/**
 * A field a module offers for filtering.
 *
 * Declared once per module and consumed by the filter builder, the column
 * chooser and the sort control. Three features, one declaration.
 */
export interface FieldDefinition {
  /** Stable key. Encoded into URLs and stored in saved views. */
  name: string;
  label: string;
  type: FieldType;
  /**
   * The values to pick from. For `enum`, and for a `tag` whose values are a
   * known set — a product's collections — rather than free text.
   *
   * Written into the declaration where the set is fixed. Where it is data,
   * the section supplies it at runtime and it arrives here the same way.
   */
  options?: FieldOption[];
  /** False for computed fields that can be shown but not filtered. */
  filterable?: boolean;
  /**
   * False for a field that can be filtered but has nothing to show: it is
   * joined in where the filter runs, and a list row does not carry it. An
   * order's `product` is the case — the row holds no line items — so offering
   * it as a column fills the column with dashes. It is left out of the column
   * chooser, the default columns and the export; the filter builder still
   * offers it. The opposite of `filterable: false`.
   */
  displayable?: boolean;
  sortable?: boolean;
  /**
   * Offered in the column chooser but left out of `defaultColumns` — a field
   * worth having, not worth opening the list to. `tags` is the first case:
   * pills make every row tall, so Shopify's own list keeps it out of the
   * default view and in the chooser only (#354). A stored column preference
   * in `localStorage` still wins, since this only shapes the starting set.
   */
  defaultHidden?: boolean;
  /**
   * A tag column shows every tag, wrapping onto more lines, instead of
   * three and a count. For columns whose tags are the point (#564).
   */
  showAllTags?: boolean;
}

/** One `field operator value` clause. */
export interface FilterCondition {
  kind: "condition";
  field: string;
  operator: FilterOperator;
  /**
   * Absent for `none` arity, a single value for `one`, a two-element array for
   * `two`, an array for `many`. `Json` rather than a union of those shapes so
   * a condition survives a round trip through a URL or a `jsonb` column
   * without a decoder that has to know the operator first.
   */
  value?: Json;
}

/**
 * Conditions joined by one combinator, nestable one inside another.
 *
 * AND-OR grouping is a tree because "COD orders in Jaipur or Mumbai that are
 * unfulfilled" is not expressible as a flat list, and that is an ordinary
 * question rather than an exotic one.
 */
export interface FilterGroup {
  kind: "group";
  combinator: "and" | "or";
  children: FilterNode[];
}

export type FilterNode = FilterCondition | FilterGroup;

/** The root of a filter. Always a group, even with one condition in it. */
export type Filter = FilterGroup;

export function isGroup(node: FilterNode): node is FilterGroup {
  return node.kind === "group";
}

export function isCondition(node: FilterNode): node is FilterCondition {
  return node.kind === "condition";
}

/** An empty filter. Matches everything. */
export function emptyFilter(): Filter {
  return { kind: "group", combinator: "and", children: [] };
}

export function operatorsFor(type: FieldType): OperatorDefinition[] {
  return OPERATORS_BY_FIELD_TYPE[type];
}

/**
 * The operator a new condition on a field of this type starts with.
 *
 * The first one the type offers, except an enum: "Status is In transit" is one
 * value, and the question people bring to a status is nearly always "any of
 * these" — in transit, out for delivery or attempted. Starting on `is` made
 * that three conditions, which AND to nothing, and the way out of that was to
 * flip the whole filter to OR, which then took the date it was meant to
 * narrow by along with it (#492). `is` is still in the list, one choice
 * away; only the starting point moves. The mobile filter sheet already works
 * this way, ticking boxes into `is_any_of`.
 */
export function defaultOperatorFor(type: FieldType): FilterOperator {
  if (type === "enum") return "is_any_of";
  return OPERATORS_BY_FIELD_TYPE[type][0]?.operator ?? "is";
}

export function operatorDefinition(
  type: FieldType,
  operator: FilterOperator,
): OperatorDefinition | undefined {
  return OPERATORS_BY_FIELD_TYPE[type].find((o) => o.operator === operator);
}

/**
 * How many conditions the filter holds, at any depth.
 *
 * The toolbar shows this. "Filter (3)" is the difference between someone
 * knowing why a list looks short and someone concluding their data is gone.
 */
export function countConditions(node: FilterNode): number {
  if (isCondition(node)) return 1;
  return node.children.reduce((sum, child) => sum + countConditions(child), 0);
}

/** Whether a filter would narrow anything. */
export function isEmptyFilter(filter: Filter): boolean {
  return countConditions(filter) === 0;
}
